import { Phone, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { openWhatsApp, emergencyMessage, sendLocationToWhatsApp } from "@/lib/whatsapp";

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-display font-bold uppercase tracking-wide transition-all duration-200 active:scale-95 focus-visible:outline-none";

// --- Call ---
export function CallButton({ size = "md", full = false, testId = "call-now-btn", label }) {
  const { t } = useLang();
  const pad = size === "lg" ? "px-7 py-4 text-base" : size === "sm" ? "px-4 py-2.5 text-xs" : "px-6 py-3 text-sm";
  return (
    <a
      href={company.phoneHref}
      data-testid={testId}
      className={`${base} ${pad} bg-amber text-carbon shadow-[0_0_0_0_rgba(246,184,0,0.5)] hover:bg-amber/90 hover:shadow-[0_8px_30px_-8px_rgba(246,184,0,0.55)] ${full ? "w-full" : ""}`}
    >
      <Phone className="h-4 w-4" strokeWidth={2.4} />
      {label || t.common.callNow}
    </a>
  );
}

// --- WhatsApp ---
export function WhatsAppButton({ size = "md", full = false, testId = "whatsapp-btn", label, message }) {
  const { t, lang } = useLang();
  const pad = size === "lg" ? "px-7 py-4 text-base" : size === "sm" ? "px-4 py-2.5 text-xs" : "px-6 py-3 text-sm";
  const onClick = () => {
    toast(t.whatsappNotice);
    openWhatsApp(message || emergencyMessage(lang));
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${base} ${pad} bg-whatsapp text-carbon hover:bg-whatsapp/90 ${full ? "w-full" : ""}`}
    >
      <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
      {label || t.common.whatsapp}
    </button>
  );
}

// --- Send location ---
export function LocationButton({ size = "md", full = false, variant = "outline", testId = "send-location-btn", label }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const pad = size === "lg" ? "px-7 py-4 text-base" : size === "sm" ? "px-4 py-2.5 text-xs" : "px-6 py-3 text-sm";
  const skin =
    variant === "solid"
      ? "bg-signal text-ice hover:bg-signal/90"
      : "border border-amber text-amber hover:bg-amber/10";
  const onClick = () => {
    sendLocationToWhatsApp({
      lang,
      onStart: () => setLoading(true),
      onError: (msg) => {
        setLoading(false);
        toast.error(msg, { duration: 7000 });
      },
    });
    setTimeout(() => setLoading(false), 12000);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      data-testid={testId}
      className={`${base} ${pad} ${skin} ${full ? "w-full" : ""} disabled:opacity-70`}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" strokeWidth={2.4} />}
      {label || t.common.sendLocation}
    </button>
  );
}
